import type { GameHistoryPort } from "./GameHistoryPort";
import type { InteractionBlockStore } from "../interaction-block/InteractionBlockStore";
import type { MoneyFormatter } from "../money/MoneyFormatter";
import type { GameHistoryProvider } from "./GameHistoryProvider";
import type { Log } from "@slot/presentation";
import type {
  GameHistoryData,
  GameHistoryTab,
  FormattedHistoryEntry,
  GameHistoryEntry,
} from "./GameHistoryTypes";

export class GameHistoryPresenter {
  private readonly buildReplayUrl: (roundId: string) => string | null;
  // Tuned for the backend's documented hard cap (`limit` clamped to 50). Same
  // value used for both the initial page and load-more pages — keeps the
  // termination check simple (`receivedCount < PAGE_SIZE` → no more data).
  private static readonly PAGE_SIZE = 50;

  private readonly panel: GameHistoryPort;
  private readonly interactionBlockStore: InteractionBlockStore;
  private readonly formatter: MoneyFormatter;
  private readonly historyProvider: GameHistoryProvider;
  private readonly log: Log;

  private open = false;
  /** Bumped by every close: a reply that lands after the page it was fetched for closed (or closed and reopened) is dropped. */
  private openGeneration = 0;
  private activeTab: GameHistoryTab = "bet_history";
  private cachedData: GameHistoryData | null = null;

  // Pagination state for the bet_history tab. Biggest-wins tabs are bounded
  // and don't paginate.
  private betHistoryCursor: string | null = null;
  private betHistoryHasMore = false;
  private fetchInFlight = false;

  public constructor(input: {
    panel: GameHistoryPort;
    interactionBlockStore: InteractionBlockStore;
    formatter: MoneyFormatter;
    log: Log;
    historyProvider: GameHistoryProvider;
    /** The page URL that replays a round, for the click-to-replay link; null = no link. */
    buildReplayUrl: (roundId: string) => string | null;
    /** Opens a row's replay link when the element reports a press; the navigation is the composition root's (decision 34). */
    openReplay: (replayUrl: string) => void;
  }) {
    this.buildReplayUrl = input.buildReplayUrl;
    this.panel = input.panel;
    this.interactionBlockStore = input.interactionBlockStore;
    this.formatter = input.formatter;
    this.historyProvider = input.historyProvider;
    this.log = input.log;

    this.panel.bind({
      onClose: () => this.close(),
      onTabBetHistory: () => this.switchTab("bet_history"),
      onTabBiggestWinsCurrent: () => this.switchTab("biggest_wins_current"),
      onLoadMore: () => void this.loadMore(),
      onReplay: (replayUrl) => input.openReplay(replayUrl),
    });
  }

  public start(): void {
    this.open = false;
    this.panel.setOpen(false);
  }

  public async openPage(): Promise<void> {
    const generation = this.openGeneration;
    this.open = true;
    this.activeTab = "bet_history";
    this.cachedData = null;
    this.betHistoryCursor = null;
    this.betHistoryHasMore = false;
    this.fetchInFlight = false;

    this.panel.setOpen(true);
    this.panel.setActiveTab(this.activeTab);
    this.panel.setLoading(true);
    this.panel.setLoadMoreVisible(false);

    this.interactionBlockStore.add("game_history");

    this.log("[GameHistory] opening, fetching initial page...");

    let data: GameHistoryData;
    try {
      data = await this.historyProvider.fetchInitialPage({
        pageSize: GameHistoryPresenter.PAGE_SIZE,
      });
      this.log(
        `[GameHistory] fetched: betHistory=${data.betHistory.length}, ` +
        `biggestAll=${data.biggestWinsAllGames.length}, ` +
        `biggestCurrent=${data.biggestWinsCurrentGame.length}`
      );
    } catch (e) {
      this.log(`[GameHistory] fetch failed: ${String(e)}`);
      data = { betHistory: [], biggestWinsAllGames: [], biggestWinsCurrentGame: [] };
    }

    // Closed, or closed and reopened, while the request ran: the rows and flags belong to the open that is showing.
    if (generation !== this.openGeneration) return;

    this.cachedData = data;
    this.updateCursorState(data.betHistory);

    this.panel.setLoading(false);
    this.renderActiveTab();
    this.panel.resetScrollToTop();
  }

  public close(): void {
    if (!this.open) return;

    this.openGeneration++;
    this.open = false;
    this.panel.setOpen(false);
    this.panel.setLoadMoreVisible(false);
    // A load-more in flight is dropped with the page: its reply no longer ends the loading state.
    this.panel.setLoadMoreLoading(false);
    this.cachedData = null;
    this.betHistoryCursor = null;
    this.betHistoryHasMore = false;
    this.fetchInFlight = false;
    this.interactionBlockStore.remove("game_history");

    this.log("[GameHistory] closed");
  }

  public isOpen(): boolean {
    return this.open;
  }

  public stop(): void {
    this.close();
  }

  private switchTab(tab: GameHistoryTab): void {
    if (tab === this.activeTab) return;

    this.activeTab = tab;
    this.panel.setActiveTab(tab);
    this.renderActiveTab();
    this.panel.resetScrollToTop();

    this.log(`[GameHistory] switched to tab: ${tab}`);
  }

  private async loadMore(): Promise<void> {
    if (this.activeTab !== "bet_history") return;
    if (!this.betHistoryHasMore) return;
    if (this.fetchInFlight) return;
    if (!this.betHistoryCursor) return;
    if (!this.cachedData) return;

    const generation = this.openGeneration;
    const cursor = this.betHistoryCursor;
    this.fetchInFlight = true;
    this.panel.setLoadMoreLoading(true);

    this.log(`[GameHistory] loading more, cursor=${cursor}`);

    let more: GameHistoryEntry[] | null;
    try {
      more = await this.historyProvider.fetchMoreBetHistory({
        beforeRoundId: cursor,
        pageSize: GameHistoryPresenter.PAGE_SIZE,
      });
    } catch (e) {
      this.log(`[GameHistory] load-more failed: ${String(e)}`);
      // Keep the cursor — a transient failure shouldn't end pagination
      // permanently. The button stays visible so the user can retry.
      more = null;
    }

    // Closed, or closed and reopened, while the request ran: the rows, the cursor and the button are another open's.
    if (generation !== this.openGeneration) return;

    this.fetchInFlight = false;
    this.panel.setLoadMoreLoading(false);
    if (more !== null) {
      this.cachedData.betHistory.push(...more);
      this.updateCursorState(more);

      // Re-render only when the user is still on the bet_history tab. If they
      // switched mid-fetch, the cached array is updated for next time but we
      // skip the row append so the alternate tab's UI isn't corrupted.
      if (this.activeTab === "bet_history") {
        const formatted = this.formatEntries(more);
        this.panel.appendEntries(formatted);
      }

      this.log(`[GameHistory] loaded ${more.length} more, hasMore=${this.betHistoryHasMore}`);
    }
    this.updateLoadMoreVisibility();
  }

  private updateCursorState(receivedEntries: GameHistoryEntry[]): void {
    this.betHistoryHasMore = receivedEntries.length === GameHistoryPresenter.PAGE_SIZE;
    this.betHistoryCursor =
      this.betHistoryHasMore && receivedEntries.length > 0
        ? receivedEntries[receivedEntries.length - 1].roundId
        : null;
  }

  private updateLoadMoreVisibility(): void {
    const visible =
      this.activeTab === "bet_history" &&
      this.betHistoryHasMore &&
      this.cachedData !== null;
    this.panel.setLoadMoreVisible(visible);
  }

  private renderActiveTab(): void {
    if (!this.cachedData) {
      this.panel.setEmptyState(true);
      this.panel.setLoadMoreVisible(false);
      return;
    }

    const entries = this.getEntriesForTab(this.activeTab);
    const formatted = this.formatEntries(entries);
    this.panel.setEntries(formatted);
    this.updateLoadMoreVisibility();
  }

  private getEntriesForTab(tab: GameHistoryTab): GameHistoryEntry[] {
    if (!this.cachedData) return [];

    switch (tab) {
      case "bet_history":
        return this.cachedData.betHistory;
      case "biggest_wins_current":
        return this.cachedData.biggestWinsCurrentGame;
    }
  }

  private formatEntries(entries: GameHistoryEntry[]): FormattedHistoryEntry[] {
    const formatter = this.formatter;

    return entries.map((entry) => {
      const featureName =
        typeof entry.featureIdentifier === "string" && entry.featureIdentifier.length > 0
          ? humanizeFeatureIdentifier(entry.featureIdentifier)
          : null;

      const chargeSource =
        typeof entry.chargeAmountMinor === "string" && entry.chargeAmountMinor.length > 0
          ? entry.chargeAmountMinor
          : entry.betAmountMinor;

      const displayedGameName =
        featureName && entry.gameName.length > 0
          ? `${entry.gameName}\n${featureName}`
          : (featureName ?? entry.gameName);

      // A failed round was closed by the backend's recovery before the spin's result, so the backend holds no replay of it
      // (`/api/replay/<id>` answers 404 `replay_unavailable` for every one): its row gets no link (review 2026-09-27 #7).
      const isFailed = entry.status === "failed";

      return {
        gameName: displayedGameName,
        dateText: this.formatRowDate(entry.date),
        betText: formatter.formatMinor(chargeSource),
        winText: formatter.formatMinor(entry.winAmountMinor),
        roundId: `id: ${entry.roundId}`,
        // The link is the boot's `buildReplayUrl`, never the provider's `replayUrl`: the backend sends its
        // API path (`/api/replay/<id>`), and a title's offline fixture sends none.
        replayUrl: entry.roundId && !isFailed ? this.buildReplayUrl(entry.roundId) : null,
        isFailed,
      };
    });
  }

  private formatRowDate(rawDate: string): string {
    // Backend emits ISO 8601 UTC ("2026-05-04T19:20:12.095Z"); fixtures emit a
    // naive local-time string ("2025-04-15 14:32"). `new Date(...)` parses both:
    // the former as UTC, the latter as local time on every modern browser engine.
    // Local-time getters then return values in the player's timezone, so we render
    // a single canonical "YYYY-MM-DD HH:MM:SS" shape regardless of source, named with
    // the zone in force at that moment: a round from before a daylight-saving change
    // keeps its own offset's name, never today's.
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return rawDate;

    const yyyy = parsed.getFullYear();
    const mm = String(parsed.getMonth() + 1).padStart(2, "0");
    const dd = String(parsed.getDate()).padStart(2, "0");
    const hh = String(parsed.getHours()).padStart(2, "0");
    const mi = String(parsed.getMinutes()).padStart(2, "0");
    const ss = String(parsed.getSeconds()).padStart(2, "0");

    const localDateText = `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    const zone = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(parsed)
      .find((part) => part.type === "timeZoneName")?.value;
    return zone ? `${localDateText} ${zone}` : localDateText;
  }
}

// Backend featureIdentifier strings are snake_case ids (e.g. "super_bonus_buy",
// "bonus_hunt_mode"). The history panel displays a human label in the leftmost
// column when a row was a bonus-buy / mode round; this is the simplest mapping
// and matches the existing non-localized gameName display. Localization can be
// layered on later via shell locale keys (e.g. "history.feature.<id>").
function humanizeFeatureIdentifier(featureIdentifier: string): string {
  return featureIdentifier
    .split("_")
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}
